import { type FC, useEffect, useState } from "react";
import { useScroll } from "../../../contexts/useScroll";
import LogoIcon from "../../../components/icons/LogoIcon";

interface ChatMessageListToolCallItemProps {
  toolName: string;
  isTyping: boolean;
  isExiting?: boolean;
  onFinished?: () => void;
}

const ChatMessageListToolCallItem: FC<ChatMessageListToolCallItemProps> = ({ toolName, isTyping, isExiting, onFinished }) => {
  const { scrollToBottom } = useScroll();
  const [removed, setRemoved] = useState(false);

  // Strip HTML tags from tool name
  const cleanToolName = toolName.replace(/<[^>]*>/g, '');

  useEffect(() => {
    if (!isTyping) {
      const timer = setTimeout(() => {
        onFinished?.();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isTyping, onFinished]);

  useEffect(() => {
    // Scroll to bottom when component mounts or updates
    setTimeout(scrollToBottom, 0);
  }, [scrollToBottom]);

  if (removed) {
    return null;
  }

  return (
    <div
      className={`flex gap-x-4 overflow-hidden ${isExiting ? 'animate-fade-out' : 'animate-fade-in'}`}
      data-testid={`tool-call-item`}
      onAnimationEnd={(): void => {
        if (isExiting) {
          setRemoved(true);
        }
      }}
    >
      <div className="shrink-0">
        <LogoIcon />
      </div>
      <div className="flex flex-col gap-y-2">
        <div className="flex items-center gap-x-2 text-sm text-lastbot-icon-muted py-2">
          <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${isTyping ? 'bg-lastbot-warning animate-pulse' : 'bg-lastbot-success'}`}></div>
          <code className="bg-lastbot-surface-subtle px-2 py-1 rounded-sm text-xs font-mono text-lastbot-on-surface-soft">
            {cleanToolName}
          </code>
          {isTyping && (
            <div className="flex space-x-1 ml-1">
              <div className="w-1 h-1 bg-lastbot-warning rounded-full animate-bounce"></div>
              <div className="w-1 h-1 bg-lastbot-warning rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-1 h-1 bg-lastbot-warning rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessageListToolCallItem; 